import { FC, Fragment, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';

import { LoadingImage } from 'src/components/loading-image';
import { usePreloadImages } from 'src/lib/hooks/use-preload-images';

import { styles } from './styles';
import { RoomImage } from './types';

interface RoomImageCarouselProps {
    images: RoomImage[];
}

export const RoomImageCarousel: FC<RoomImageCarouselProps> = ({ images }) => {
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Users browse this one room's images, so fetch them all up front rather than on each click
    usePreloadImages(images.map(({ src }) => src));

    if (images.length === 0) return null;

    // The image list can shrink (e.g. a different room), so keep the index in range for
    // rendering and navigation rather than trusting the stored value
    const activeIndex = selectedIndex < images.length ? selectedIndex : 0;
    const hasMultipleImages = images.length > 1;
    const activeImage = images[activeIndex];

    const showPrevious = () =>
        setSelectedIndex(activeIndex === 0 ? images.length - 1 : activeIndex - 1);
    const showNext = () => setSelectedIndex((activeIndex + 1) % images.length);

    return (
        <Box css={styles.carousel}>
            <Box css={styles.carouselMain}>
                <LoadingImage
                    key={activeImage.src}
                    src={activeImage.src}
                    alt={activeImage.alt}
                    imageStyles={styles.carouselImage}
                    placeholderSrc={activeImage.thumbnailSrc}
                />

                {hasMultipleImages && (
                    <Fragment>
                        <IconButton
                            css={[styles.carouselControl, styles.carouselControlPrev]}
                            onClick={showPrevious}
                            aria-label={t('view_room_previous_image')}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            css={[styles.carouselControl, styles.carouselControlNext]}
                            onClick={showNext}
                            aria-label={t('view_room_next_image')}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                        <Typography variant="body3" css={styles.carouselCounter}>
                            {activeIndex + 1} / {images.length}
                        </Typography>
                    </Fragment>
                )}
            </Box>

            {hasMultipleImages && (
                <Box css={styles.thumbnails}>
                    {images.map((image, index) => (
                        <button
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${image.src}-${index}`}
                            type="button"
                            css={[
                                styles.thumbnail,
                                index === activeIndex && styles.thumbnailActive,
                            ]}
                            onClick={() => setSelectedIndex(index)}
                            aria-label={image.alt}
                            aria-current={index === activeIndex}
                        >
                            <LoadingImage
                                key={image.thumbnailSrc}
                                src={image.thumbnailSrc}
                                alt=""
                                imageStyles={styles.thumbnailImage}
                                priority="low"
                            />
                        </button>
                    ))}
                </Box>
            )}
        </Box>
    );
};
